import { boardCopy } from "./chooser";

// Carrying a board to a new place, in Choose boards and in the overview.
// Direct, as on a phone's home screen: a board picked up lifts off the
// grid and follows the pointer, the boards around it slide aside to show
// where it would land, and let go it settles into that place and is lit
// a moment, its number already the new one. With a mouse it is picked up
// by dragging; on a phone by holding it, since a swipe there is the list
// scrolling. Carried to the list's edge, the list scrolls on.
//
// Each place to carry in is a surface: its list of cards, what scrolls,
// what is carried along with a card and what a card is dropped beside,
// and what happens when a card is let go or put back.

export default {
  // Choose boards: a slide of several boards (the product pages) moves as
  // one, and lands before or after another slide's boards, never among
  // them.
  dragStart(event) {
    const card = event.target.closest(".deck-chooser-row");
    if (!card || event.target.closest("input")) return;
    const list = this.chooserListTarget;
    const slide = (other) => [
      ...list.querySelectorAll(
        `.deck-chooser-row[data-key="${other.dataset.key}"]`,
      ),
    ];
    this.pickUp(event, card, {
      list,
      scroller: list,
      card: ".deck-chooser-row",
      thumb: (other) => other.querySelector(".deck-chooser-thumb"),
      ghost: (other) =>
        other.querySelector(".deck-chooser-thumb").cloneNode(true),
      carried: slide,
      beside: slide,
      renumber: () => this.numberCards(),
      dropped: (drag) => this.chooserDropped(drag),
      putBack: () => this.renderChooser(),
    });
  },

  chooserDropped(drag) {
    if (!drag.moved) return this.renderChooser();
    this.choice = [...this.chooserListTarget.children].map(
      (card) => this.choice[Number(card.dataset.row)],
    );
    this.renderChooser();
    const landed = [
      ...this.chooserListTarget.querySelectorAll(
        `.deck-chooser-row[data-key="${drag.key}"]`,
      ),
    ];
    landed.forEach((card) => card.classList.add("is-dropped"));
    const first = landed[0];
    if (this.hasChooserSaidTarget && first) {
      this.chooserSaidTarget.textContent = this.chooserSaidTarget.dataset.moved
        .replace(
          "%{name}",
          first.querySelector(".deck-chooser-name").lastChild.textContent,
        )
        .replace(
          "%{position}",
          Number(first.querySelector(".deck-chooser-number").textContent),
        );
    }
  },

  // The overview: a board moves with the other pages of its slide, and a
  // chapter's opening page carries the whole chapter. The cover opens the
  // deck and stays where it is.
  overviewDragStart(event) {
    if (!this.overview || !this.arranging) return;
    const tile = event.target.closest(".deck-tile");
    if (
      !tile ||
      "cover" in tile.dataset ||
      event.target.closest("button, a, input")
    )
      return;
    const list = this.canvasTarget;
    // The tiles either side of one that belong with it, up to the cover.
    const run = (other, same) => {
      const tiles = [...list.children];
      const along = (next) => next && !("cover" in next.dataset) && same(next);
      let first = tiles.indexOf(other);
      let last = first;
      while (along(tiles[first - 1])) first -= 1;
      while (along(tiles[last + 1])) last += 1;
      return tiles.slice(first, last + 1);
    };
    const slide = (other) =>
      run(other, (next) => next.dataset.key === other.dataset.key);
    const chapter = (other) =>
      other.dataset.chapter
        ? run(other, (next) => next.dataset.chapter === other.dataset.chapter)
        : [other];
    const chapterWide = "opener" in tile.dataset;
    this.pickUp(event, tile, {
      list,
      scroller: this.stageTarget,
      card: ".deck-tile",
      fixed: (other) => "cover" in other.dataset,
      thumb: (other) =>
        other.querySelector('[data-presentation-target="slide"]'),
      ghost: (other) => {
        const board = other.querySelector('[data-presentation-target="slide"]');
        const frame = document.createElement("div");
        frame.className = "deck-tile-ghost";
        frame.style.setProperty(
          "--deck-tile-zoom",
          this.canvasTarget.style.getPropertyValue("--deck-tile-zoom"),
        );
        frame.append(boardCopy(board, "carried"));
        return frame;
      },
      carried: chapterWide ? chapter : slide,
      beside: chapterWide ? chapter : slide,
      renumber: () => this.renderTiles(),
      dropped: (drag) => this.overviewDropped(drag),
      putBack: (drag) => {
        list.append(...drag.before);
        this.renderTiles();
      },
    });
  },

  async overviewDropped(drag) {
    if (!drag.moved) return;
    this.afterReorder();
    // Lit where it landed, again if it was lit a moment ago.
    this.canvasTarget.querySelectorAll(".deck-tile.is-dropped").forEach((tile) => tile.classList.remove("is-dropped"));
    void this.canvasTarget.offsetWidth;
    drag.cards.forEach((tile) => tile.classList.add("is-dropped"));
    if (!(await this.saveOverviewOrder(drag.before))) return;
    const first = drag.cards[0];
    this.overviewSay(
      this.overviewBarTarget.dataset.moved
        .replace("%{name}", first.querySelector(".deck-tile-name").textContent)
        .replace(
          "%{position}",
          Number(first.querySelector(".deck-tile-number").textContent),
        ),
    );
  },

  pickUp(event, card, surface) {
    if (event.button > 0 || this.dragging) return;
    this.dragging = {
      card,
      surface,
      x: event.clientX,
      y: event.clientY,
      touch: event.pointerType === "touch",
      started: false,
    };
    this.boundDragMove ||= this.dragMove.bind(this);
    this.boundDragEnd ||= this.dragEnd.bind(this);
    this.boundDragCancel ||= () => this.cancelDrag();
    this.boundTouchHold ||= (touch) =>
      this.dragging?.started && touch.preventDefault();
    document.addEventListener("pointermove", this.boundDragMove);
    document.addEventListener("pointerup", this.boundDragEnd);
    document.addEventListener("pointercancel", this.boundDragCancel);
    surface.list.addEventListener("touchmove", this.boundTouchHold, {
      passive: false,
    });
    if (this.dragging.touch) {
      card.classList.add("is-pressing");
      this.dragging.hold = setTimeout(() => this.lift(), 300);
    }
  },

  dragMove(event) {
    const drag = this.dragging;
    if (!drag) return;
    const moved = Math.hypot(event.clientX - drag.x, event.clientY - drag.y);
    if (!drag.started) {
      // A finger that moves before the hold is up is scrolling the list.
      if (drag.touch) {
        if (moved > 8) this.releaseDrag();
        return;
      }
      if (moved < 6) return;
      this.lift();
    }
    drag.pointer = { x: event.clientX, y: event.clientY };
    drag.ghost.style.transform = `translate(${event.clientX - drag.offsetX}px, ${event.clientY - drag.offsetY}px)`;
    this.makeRoom(event.clientX, event.clientY);
    this.scrollWhileDragging();
  },

  // Off the grid: the cards carried stay as empty slots, and a copy of the
  // board rises under the pointer.
  lift() {
    const drag = this.dragging;
    if (!drag || drag.started) return;
    const { surface } = drag;
    drag.started = true;
    drag.key = drag.card.dataset.key;
    drag.before = [...surface.list.children];
    drag.cards = surface.carried(drag.card);
    const rect = surface.thumb(drag.card).getBoundingClientRect();
    drag.offsetX = drag.x - rect.left;
    drag.offsetY = drag.y - rect.top;
    drag.ghost = document.createElement("div");
    drag.ghost.className = "deck-chooser-ghost";
    drag.ghost.style.transform = `translate(${rect.left}px, ${rect.top}px)`;
    drag.ghost.append(surface.ghost(drag.card));
    if (drag.cards.length > 1) {
      const count = document.createElement("span");
      count.className = "deck-chooser-ghost-count";
      count.textContent = drag.cards.length;
      drag.ghost.append(count);
    }
    document.body.append(drag.ghost);
    drag.card.classList.remove("is-pressing");
    drag.cards.forEach((card) => card.classList.add("is-placeholder"));
    surface.list.classList.add("is-dragging");
  },

  // The slot moves to where the board would land: past the cards under
  // the pointer, in the direction it is being carried. The cards it passes
  // slide over to make room, and nothing is decided again until they have.
  makeRoom(x, y) {
    const drag = this.dragging;
    const { surface } = drag;
    if (performance.now() < (drag.settling || 0)) return;
    const card = document.elementFromPoint(x, y)?.closest(surface.card);
    if (
      !card ||
      !surface.list.contains(card) ||
      drag.cards.includes(card) ||
      surface.fixed?.(card)
    )
      return;

    const cards = [...surface.list.children];
    const run = surface.beside(card);
    const forward = cards.indexOf(card) > cards.indexOf(drag.cards[0]);
    this.flip(surface.list, () => {
      if (forward) run[run.length - 1].after(...drag.cards);
      else run[0].before(...drag.cards);
    });
    drag.moved = true;
    drag.settling = performance.now() + 200;
    surface.renumber();
  },

  // Cards that changed place glide from where they were to where they are.
  flip(list, change) {
    const cards = [...list.children];
    const before = new Map(
      cards.map((card) => [card, card.getBoundingClientRect()]),
    );
    change();
    if (this.calm) return;
    cards.forEach((card) => {
      const was = before.get(card);
      const now = card.getBoundingClientRect();
      const dx = was.left - now.left;
      const dy = was.top - now.top;
      if (!dx && !dy) return;
      card.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }],
        { duration: 200, easing: "cubic-bezier(0.2, 0, 0, 1)" },
      );
    });
  },

  // Carried to the top or bottom of the list, the list scrolls on.
  scrollWhileDragging() {
    if (this.dragScroll) return;
    const step = () => {
      const drag = this.dragging;
      if (!drag?.started || !drag.pointer) {
        this.dragScroll = null;
        return;
      }
      const { scroller } = drag.surface;
      const list = scroller.getBoundingClientRect();
      const edge = 64;
      const speed =
        drag.pointer.y < list.top + edge
          ? -14
          : drag.pointer.y > list.bottom - edge
            ? 14
            : 0;
      if (speed) {
        scroller.scrollTop += speed;
        this.makeRoom(drag.pointer.x, drag.pointer.y);
      }
      this.dragScroll = requestAnimationFrame(step);
    };
    this.dragScroll = requestAnimationFrame(step);
  },

  // Let go: the copy settles into the slot, and the surface takes the
  // order as the cards stand.
  dragEnd() {
    const drag = this.dragging;
    if (!drag) return;
    if (!drag.started) {
      this.releaseDrag();
      return;
    }
    this.swallowClick();
    const slot = drag.surface.thumb(drag.cards[0]).getBoundingClientRect();
    const ghost = drag.ghost;
    drag.ghost = null;
    const land = () => {
      ghost.remove();
      drag.cards.forEach((card) => card.classList.remove("is-placeholder"));
      drag.surface.dropped(drag);
    };
    this.releaseDrag({ keepSlot: true });
    if (this.calm) {
      land();
    } else {
      ghost.classList.add("is-settling");
      ghost
        .animate(
          [
            { transform: ghost.style.transform },
            { transform: `translate(${slot.left}px, ${slot.top}px)` },
          ],
          {
            duration: 220,
            easing: "cubic-bezier(0.2, 0, 0, 1)",
            fill: "forwards",
          },
        )
        .finished.then(land, land);
    }
  },

  // Esc, or the browser taking the pointer back: the board goes back where
  // it was.
  cancelDrag() {
    const drag = this.dragging;
    this.releaseDrag();
    if (drag?.started) drag.surface.putBack(drag);
  },

  // A drag ends on a card; its click is not a tick, nor a pick.
  swallowClick() {
    const swallow = (click) => {
      click.preventDefault();
      click.stopPropagation();
    };
    document.addEventListener("click", swallow, { capture: true, once: true });
    setTimeout(
      () => document.removeEventListener("click", swallow, { capture: true }),
      0,
    );
  },

  releaseDrag({ keepSlot = false } = {}) {
    const drag = this.dragging;
    if (!drag) return;
    const { list } = drag.surface;
    clearTimeout(drag.hold);
    cancelAnimationFrame(this.dragScroll);
    this.dragScroll = null;
    drag.ghost?.remove();
    document.removeEventListener("pointermove", this.boundDragMove);
    document.removeEventListener("pointerup", this.boundDragEnd);
    document.removeEventListener("pointercancel", this.boundDragCancel);
    list.removeEventListener("touchmove", this.boundTouchHold);
    list.classList.remove("is-dragging");
    list
      .querySelectorAll(
        keepSlot ? ".is-pressing" : ".is-placeholder, .is-pressing",
      )
      .forEach((card) =>
        card.classList.remove("is-placeholder", "is-pressing"),
      );
    this.dragging = null;
  },
};
